import React from 'react';
import Map from './Map'
import Sidebar from './Sidebar';
import { isValidCoordinate, describeInvalidPoint } from '../utils/coordinates';

const Container: React.FunctionComponent<any> = (data) => {
  const [initialized, setInitialized] = React.useState(false);
  const [points, setPoints] = React.useState<any>([]);
  const [newsItems, setNewsItems] = React.useState<any>([]);
  const [sideBarPoints, setSideBarPoints] = React.useState([]);
  const [selectedPoint, setSelectedPoint] = React.useState(null);
  const [mapType, setMapType] = React.useState<string | null>(null);
  const [showingCGI, setShowingCGI] = React.useState(true);
  const [showingMGI, setShowingMGI] = React.useState(true);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const typeParam = urlParams.get('type');

    setMapType(typeParam);

    if (data?.data?.value) {
      const mayors = data?.data?.value?.mayors || [];
      const cgiOfficials = data?.data?.value?.['cgi-officials'] || [];
      const lgiOfficials = data?.data?.value?.['lgi-officials'] || [];

      const combinedPoints = [
        ...mayors.map((m: any) => {
          return {
            ...m,
            isCGI: false,
            isLGI: false
          }
        }),
        ...cgiOfficials.map((c: any) => {
          return {
            ...c,
            isCGI: true,
            isLGI: false
          }
        }),
        ...lgiOfficials
      ].map((point: any, index: number) => {
        return {
          ...point,
          id: index
        }
      }).sort((a: any, b: any) => {
        if (a.state < b.state) {
          return -1;
        } else if (a.state > b.state) {
          return 1;
        } else {
          return 0;
        }
      });

      const validPoints: any[] = [];
      const invalidPoints: any[] = [];
      for (const point of combinedPoints) {
        if (isValidCoordinate(point.latitude, point.longitude)) {
          validPoints.push(point);
        } else {
          invalidPoints.push(point);
        }
      }

      for (const invalidPoint of invalidPoints) {
        console.error(`[map] Skipped entry with missing/invalid coordinates — fix in Google Sheet then re-sync:${describeInvalidPoint(invalidPoint)}`);
      }

      setPoints(validPoints);
      setNewsItems([
        ...(data?.data?.value?.newsroom || []),
        ...(data?.data?.value?.['cgi-newsroom'] || []),
        ...(data?.data?.value?.['lgi-newsroom'] || [])
      ]);
    }

    if (points && !initialized) {
      setInitialized(true);
    }
    // eslint-disable-next-line
  }, [data, initialized]);

  const [showingLGI, setShowingLGI] = React.useState(true);

  React.useEffect(() => {
    if (mapType === 'mgi') {
      setShowingMGI(true);
      setShowingCGI(false);
      setShowingLGI(false);
    } else if (mapType === 'cgi') {
      setShowingMGI(false);
      setShowingCGI(true);
      setShowingLGI(false);
    } else if (mapType === 'lgi') {
      setShowingMGI(false);
      setShowingCGI(false);
      setShowingLGI(true);
    } else {
      // No type specified - show all
      setShowingMGI(true);
      setShowingCGI(true);
      setShowingLGI(true);
    }
  }, [mapType]);


  React.useEffect(() => {
    if (mapType === 'mgi') {
      setSideBarPoints(points.filter((point: any) => !point.isCGI && !point.isLGI));
    } else if (mapType === 'cgi') {
      setSideBarPoints(points.filter((point: any) => point.isCGI));
    } else if (mapType === 'lgi') {
      setSideBarPoints(points.filter((point: any) => point.isLGI));
    } else {
      setSideBarPoints(points);
    }
    // eslint-disable-next-line
  }, [points]);

  React.useEffect(() => {
    let filteredPoints = points;
    if (!showingCGI) {
      filteredPoints = filteredPoints.filter((point: any) => !point.isCGI);
    }

    if (!showingMGI) {
      filteredPoints = filteredPoints.filter((point: any) => point.isCGI || point.isLGI);
    }

    if (!showingLGI) {
      filteredPoints = filteredPoints.filter((point: any) => !point.isLGI);
    }
    setSideBarPoints(filteredPoints);
  }, [showingCGI, showingMGI, showingLGI, points]);

  const handleSelection = (mayor: any) => setSelectedPoint(mayor);

  const removeSelection = () => {
    setSelectedPoint(null);
    if (mapType === 'mgi') {
      setSideBarPoints(points.filter((point: any) => !point.isCGI && !point.isLGI));
    } else if (mapType === 'cgi') {
      setSideBarPoints(points.filter((point: any) => point.isCGI));
    } else if (mapType === 'lgi') {
      setSideBarPoints(points.filter((point: any) => point.isLGI));
    } else {
      setSideBarPoints(points);
    }
  };

  const onSearch = (searchTerm: string) => {
    if (searchTerm !== '') {
      removeSelection();
      let filteredPoints = points
        .filter((point: any) => {
          const pointString = `${point?.county?.toLowerCase()}${point?.city?.toLowerCase()}${point?.district?.toLowerCase()}${point.state.toLowerCase()}${point?.mayors_name?.toLowerCase() || point?.county_officials_name?.toLowerCase() || point?.legislators_name?.toLowerCase()}`.replace(/ /g, '').replace(/[^\w\s]/gi, '');
          return point?.city?.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0 ||
            point?.county?.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0 ||
            point?.district?.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0 ||
            point.state.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0 ||
            point?.mayors_name?.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0 ||
            point?.county_officials_name?.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0 ||
            point?.legislators_name?.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0 ||
            point.state_long.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0 ||
            pointString.indexOf(searchTerm.toLowerCase().replace(/ /g, '').replace(/[^\w\s]/gi, '')) >= 0;
        })

      if (!showingCGI && mapType) {
        filteredPoints = filteredPoints.filter((point: any) => !point.isCGI);
      }

      if (!showingMGI && mapType) {
        filteredPoints = filteredPoints.filter((point: any) => point.isCGI || point.isLGI);
      }

      if (!showingLGI && mapType) {
        filteredPoints = filteredPoints.filter((point: any) => !point.isLGI);
      }
    
      setSideBarPoints(filteredPoints);
    } else {
      // Reset to show all cards when search is cleared
      removeSelection();
      let filteredPoints = points;
      if (!showingCGI && mapType) {
        filteredPoints = filteredPoints.filter((point: any) => !point.isCGI);
      }

      if (!showingMGI && mapType) {
        filteredPoints = filteredPoints.filter((point: any) => point.isCGI || point.isLGI);
      }

      if (!showingLGI && mapType) {
        filteredPoints = filteredPoints.filter((point: any) => !point.isLGI);
      }
      setSideBarPoints(filteredPoints);
    }
  };

  const onMarkerSelect = (markerId: number) => {
    setSelectedPoint(points.filter((point: any) => point.id === markerId)[0]);
  };

  const onLegendClick = (type: string) => {
    if (mapType === 'cgi' && type === 'mgi') {
      let showing = true;
      document.querySelectorAll('.mgi-marker').forEach((marker: any) => {
        if (marker.classList.contains('show')) {
          marker.classList.remove('show');
          showing = false;
        } else {
          marker.classList.add('show');
        }
      });

      if (showing) {
        setSideBarPoints(points);
      } else {
        setSideBarPoints(points.filter((point: any) => point.isCGI));
      }
    }
    else if (mapType === 'mgi' && type === 'cgi') {
      let showing = true;
      document.querySelectorAll('.cgi-marker').forEach((marker: any) => {
        if (marker.classList.contains('show')) {
          marker.classList.remove('show');
          showing = false;
        } else {
          marker.classList.add('show');
        }
      });

      if (showing) {
        setSideBarPoints(points);
      } else {
        setSideBarPoints(points.filter((point: any) => !point.isCGI));
      }
    }
  }

  return (
    <div id={mapType ?? 'all'} className="font-primary flex flex-col-reverse md:flex-row">
      <Sidebar showingMGI={showingMGI} setShowingMGI={setShowingMGI} showingCGI={showingCGI} setShowingCGI={setShowingCGI} showingLGI={showingLGI} setShowingLGI={setShowingLGI} type={mapType} onClick={handleSelection} removePoint={removeSelection} onSearch={onSearch} selectedPoint={selectedPoint} points={sideBarPoints} news={newsItems} />
      <Map showingMGI={showingMGI} setShowingMGI={setShowingMGI} showingCGI={showingCGI} setShowingCGI={setShowingCGI} showingLGI={showingLGI} setShowingLGI={setShowingLGI} type={mapType} onClick={handleSelection} onMarkerSelect={onMarkerSelect} selectedPoint={selectedPoint} points={points} onLegendClick={onLegendClick} />
    </div>
  )
}

export default Container;
